import {
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from "@angular/core";
import { MainService } from "core/main.service";
import { BehaviorSubject, Subject } from "rxjs";
import { FileService } from "files/files.service";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { map, take, takeUntil, tap } from "rxjs/operators";
import { SelectionInput, SelectionOption } from "common/form/input/types/selection-input";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MessageType, StreamingService } from "chatbot/streaming.service";
import { marked, RendererObject } from "marked";
import type { Tokens } from "marked";
import { ChatbotResource } from "core/stages-client";
import { ChatbotInputComponent, SendButtonConfiguration } from "chatbot/chatbot-input.component";
import { SessionStorageService } from "core/session-storage.service";
import { LayoutService, Size } from "core/layout.service";
import { ButtonComponent } from "common/buttons/button.component";
import { InputComponent } from "common/form/input/input.component";
import { AsyncPipe, NgClass, NgStyle } from "@angular/common";
import { AiAcknowledgementsComponent } from "common/disclaimer/ai-acknowledgements/ai-acknowledgements.component";
import { ChatbotWelcomeComponent } from "chatbot/chatbot-welcome.component";
import { TranslateModule } from "@ngx-translate/core";
import { ChatbotMenuComponent } from "chatbot/menu/chatbot-menu.component";
import { ChatbotMessageToolbarComponent } from "chatbot/chatbot-message-toolbar.component";
import ChatMessage = stages.chatbot.ChatMessage;
import ColoredOption = stages.ColoredOption;
import Evidence = stages.chatbot.Evidence;
import Download = stages.chatbot.Download;
import AiAcknowledgementsState = stages.userservice.core.AiAcknowledgementsState;

type ErrorMessage = stages.chatbot.ErrorMessage;

export enum ChatbotChildContainer {
	CHATBOT = "chatbot",
	MENU = "menu",
	SETTINGS = "settings",
	HISTORY = "history",
	NONE = "none",
}

interface FormattedChatMessage extends ChatMessage {
	formattedValue: string;
}

@Component({
	selector: "stages-chatbot",
	templateUrl: "./chatbot.component.html",
	styleUrls: ["./chatbot.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		ButtonComponent,
		InputComponent,
		AsyncPipe,
		NgClass,
		AiAcknowledgementsComponent,
		ChatbotWelcomeComponent,
		NgStyle,
		RouterLink,
		TranslateModule,
		ChatbotInputComponent,
		ChatbotMenuComponent,
		ChatbotMessageToolbarComponent,
	],
})
export class ChatbotComponent implements OnInit, OnDestroy {
	mainService = inject(MainService);
	private chatbotResource = inject(ChatbotResource);
	private fileService = inject(FileService);
	private route = inject(ActivatedRoute);
	private streamingService = inject(StreamingService);
	private sessionStorageService = inject(SessionStorageService);
	private layoutService = inject(LayoutService);

	protected readonly ChatbotChildContainer = ChatbotChildContainer;
	private static readonly REFORMAT_LIMIT = 5;
	private static readonly SNAPIN_MARGIN = 5;
	private static readonly ERROR_MESSAGE_SYSTEM = "chatbot.error.system";
	static readonly CHAT_ID_PREF_KEY = "chatbot_chatid";
	private static readonly PLACEHOLDER_NEW_CHAT = "chatbot.input.placeholder";
	private static readonly PLACEHOLDER_FOLLOW_UP = "chatbot.input.placeholder.followUp";

	readonly chatbotPanel = viewChild.required<ElementRef>("chatbotPanel");
	private destroy$ = new Subject<boolean>();

	protected warningMessage: string | undefined;
	protected errorMessage: string | undefined;

	protected selectionInput?: SelectionInput;
	protected form = new UntypedFormGroup({});

	private currentWorkspaceId?: string;
	protected currentProcessVersion?: string;

	chatGuid?: string;
	protected chatMessages$ = new BehaviorSubject<FormattedChatMessage[]>([]);
	protected responseStream!: Promise<string>;
	protected welcomeMessage$ = new BehaviorSubject<string>("");
	protected summary$ = new BehaviorSubject<string>("");
	protected inputPlaceholder: string = ChatbotComponent.PLACEHOLDER_NEW_CHAT;

	protected isLoading$ = new BehaviorSubject<boolean>(false);
	protected isStreaming$ = new BehaviorSubject<boolean>(false);
	protected inputFocused$ = new BehaviorSubject<boolean>(false);
	private triggerInputFocus = false;

	private sendButtonConfiguration: SendButtonConfiguration = {
		buttonStyles: { transform: "rotate(-45deg)" },
		iconStyles: {},
	};

	protected sendButtonConfiguration$ = new BehaviorSubject<SendButtonConfiguration>({
		buttonStyles: {},
		iconStyles: {},
	});

	protected selectableViews: ColoredOption[] = [];
	protected selectedViewId: string | null = null;
	protected coloredOptionForSelectedView?: ColoredOption;

	/*when user must pick a view (first time using chatbot and multiple views available) */
	protected showViewChips: boolean = false;
	protected settingsOpen: boolean = false;

	aiAcknowledgementsState$ = this.mainService.applicationContext$.pipe(
		map((applicationContext) => applicationContext.aiAcknowledgementsState),
	);

	protected _aiAcknowledgementsState: AiAcknowledgementsState = "NONE";

	private evidences: Evidence[] = [];
	private downloads: Download[] = [];

	protected coloredModeActive: boolean = true;
	protected isHistoryMode: boolean = false;
	protected feedbackAllowed: boolean = false;

	views: ColoredOption[] = [];
	defaultViewId: string | null = null;

	readonly closeButtonPressed = output<void>();

	readonly setParentToFullScreen = output<boolean>();

	// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Remove this comment and fix warnings!
	async ngOnInit(): Promise<void> {
		this.currentWorkspaceId = this.route.snapshot.paramMap.get("workspaceId")!;
		this.currentProcessVersion = this.route.snapshot.paramMap.get("processVersion")!;
		this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
			this.currentWorkspaceId = params.workspaceId;
			this.currentProcessVersion = params.processVersion;
		});
		const applicationContext = await this.mainService.getApplication(this.currentWorkspaceId);
		const views = applicationContext.views;
		const defaultView = applicationContext.defaultView;

		this.aiAcknowledgementsState$.pipe(takeUntil(this.destroy$)).subscribe((aiAcknowledgementsState) => {
			this.setAiAcknowledgementsState(aiAcknowledgementsState);
		});

		this.selectableViews = views;
		if (views.length === 0) {
			console.log("This should not happen: No libraries available for chatbot");
			return;
		}

		if (defaultView?.welcomeMessage) {
			this.welcomeMessage$.next(defaultView?.welcomeMessage);
		}

		if (this.coloredModeActive) {
			this.coloredOptionForSelectedView = this.getSelectedView(defaultView?.id ?? this.selectableViews[0].id!, views);
			this.sendButtonConfiguration.iconStyles = { color: this.coloredOptionForSelectedView?.color ?? "" };
		}
		this.sendButtonConfiguration$.next(this.sendButtonConfiguration);

		this.selectedViewId = defaultView?.id ?? null;
		this.defaultViewId = this.selectedViewId;
		this.showViewChips = defaultView === null ? views.length > 1 : !this.selectedViewIsAvailable(views);
		this.registerForm(views);
		this.mainService.applicationContext$.pipe(takeUntil(this.destroy$)).subscribe((appContext) => {
			this.resetForm(appContext.views, appContext.defaultView);
		});

		const storedChatbotIds = this.sessionStorageService.getValue(ChatbotComponent.CHAT_ID_PREF_KEY, {
			chatGuid: null,
			viewId: null,
		});

		// ST-37396 - Assumption: All links in the chatbot response text (not the evidence links) are external links
		const renderer: RendererObject = {
			link(link: Tokens.Link): string {
				const titleAttr = link.title ? ` title="${link.title}"` : "";
				return `<a href="${link.href}"${titleAttr} target="_blank" rel="noopener noreferrer">${link.text}</a>`;
			},
		};

		marked.use({ renderer });

		if (storedChatbotIds.chatGuid) {
			const chat = await this.chatbotResource.getChat(storedChatbotIds.chatGuid);
			if (
				chat !== null &&
				chat.chatMessages.length > 0 &&
				this.selectableViews.some((view) => view.id === chat.chatMessages[0].viewId)
			) {
				this.chatGuid = storedChatbotIds.chatGuid;
				const formattedMessages: FormattedChatMessage[] = chat.chatMessages.map((chatMessage) =>
					this.formatChatMessage(chatMessage),
				);
				this.chatMessages$.next(formattedMessages);
				this.summary$.next(chat.summary);
				this.feedbackAllowed = chat.feedbackSystemAvailable && chat.actions.CREATE_FEEDBACK;
			}
		}
		if (!this.chatGuid && !this.showViewChips) {
			const newChat = await this.chatbotResource.newChat(this.selectedViewId!);
			this.chatGuid = newChat.chatGuid;
			this.feedbackAllowed = newChat.feedbackSystemAvailable && newChat.actions.CREATE_FEEDBACK;
		}
		this.toggleSelectionInput(this.showViewChips || this._aiAcknowledgementsState !== "ACCEPTED");

		this.chatMessages$.pipe(takeUntil(this.destroy$)).subscribe((messages) => {
			this.inputPlaceholder =
				messages.length === 0 ? ChatbotComponent.PLACEHOLDER_NEW_CHAT : ChatbotComponent.PLACEHOLDER_FOLLOW_UP;
		});
	}

	private selectedViewIsAvailable(views: ColoredOption[]): boolean {
		return views.some((view) => view.id === this.selectedViewId);
	}

	registerForm(views: ColoredOption[]): void {
		this.selectionInput = new SelectionInput({
			key: "chatViews",
			multiple: false,
			selectionOptions: setupSelectionOptions(views),
			translateKey: "chatbot.view.selection",
		});
		if (this.selectedViewId !== null) {
			this.selectionInput.setSelectionValue([this.selectedViewId]);
		}
		const viewSelectionFormControl = new UntypedFormControl(this.selectionInput.value);
		this.form.addControl(this.selectionInput.key, viewSelectionFormControl);
		viewSelectionFormControl.markAllAsTouched();

		this.form.valueChanges
			.pipe(
				// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Remove this comment and fix warnings!
				tap(async () => {
					this.showViewChips = false;
					this.selectedViewId = this.form.get(this.selectionInput!.key)!.value[0];
					if (this.coloredModeActive && this.selectedViewId !== null) {
						this.coloredOptionForSelectedView = this.getSelectedView(this.selectedViewId, views);
						this.sendButtonConfiguration.iconStyles = { color: this.coloredOptionForSelectedView?.color ?? "" };
						this.sendButtonConfiguration$.next(this.sendButtonConfiguration);
					}
					if (!this.isHistoryMode) {
						// if changes on view selection result from loading a chat history entry, do not request new chat
						await this.newChat();
					}
				}),
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				// async function is doing the job
			});
	}

	resetForm(views: ColoredOption[], defaultView: stages.ChatbotView | null): void {
		this.selectionInput!.setSelectionOptions(setupSelectionOptions(views));
		this.selectableViews = views;
		if (views.length > 0 && !this.selectedViewIsAvailable(views)) {
			this.selectedViewId = defaultView?.id ?? null;
			this.defaultViewId = this.selectedViewId;
			if (this.selectedViewId !== null) {
				this.selectionInput!.setSelectionValue([this.selectedViewId!]);
				this.form.get(this.selectionInput!.key)!.setValue(this.selectedViewId);
			}
			this.welcomeMessage$.next(defaultView?.welcomeMessage ?? "");
		}
	}

	async loadChat(): Promise<void> {
		const storedChatbotIds = this.sessionStorageService.getValue(ChatbotComponent.CHAT_ID_PREF_KEY, {
			chatGuid: null,
			viewId: null,
		});
		// show the associated view of the loaded chat
		if (this.selectionInput && storedChatbotIds.viewId) {
			this.isHistoryMode = true;
			this.selectedViewId = storedChatbotIds.viewId;
			this.selectionInput.setSelectionValue([storedChatbotIds.viewId]);
			this.form.controls[this.selectionInput.key].setValue(this.selectionInput.value);
		}

		if (storedChatbotIds.chatGuid) {
			this.chatGuid = storedChatbotIds.chatGuid;
			const chat = await this.chatbotResource.getChat(this.chatGuid);
			if (chat !== null && this.selectableViews.some((view) => view.id === chat.chatMessages[0].viewId)) {
				const formattedMessages: FormattedChatMessage[] = chat.chatMessages.map((chatMessage) =>
					this.formatChatMessage(chatMessage),
				);
				this.chatMessages$.next(formattedMessages);
				this.summary$.next(chat.summary);
				this.feedbackAllowed = chat.feedbackSystemAvailable && chat.actions.CREATE_FEEDBACK;
			}
		}
	}

	ngOnDestroy(): void {
		void this.abort(false);
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	setInitialChatView(viewId: string): void {
		this.selectionInput?.setSelectionValue([viewId]);
		this.form.setControl(this.selectionInput!.key, new UntypedFormControl(this.selectionInput!.value));
		this.toggleSelectionInput(false);
	}

	async newChat(): Promise<void> {
		if (!this.selectedViewId) {
			throw new Error("No view selected");
		}
		void this.abort(false);
		this.sessionStorageService.delValue(ChatbotComponent.CHAT_ID_PREF_KEY);
		this.triggerInputFocus = !this.triggerInputFocus;
		this.inputFocused$.next(this.triggerInputFocus);
		if (this.isHistoryMode) {
			// set default view for dropdown, like in loadChat():
			this.selectedViewId = this.defaultViewId;
			this.selectionInput!.setSelectionValue([this.defaultViewId!]);
			this.form.controls[this.selectionInput!.key].setValue(this.selectionInput!.value);
			this.isHistoryMode = false;
		}
		const chatGuidObject = await this.chatbotResource.newChat(this.selectedViewId!);
		this.chatGuid = chatGuidObject.chatGuid;
		this.welcomeMessage$.next(chatGuidObject.welcomeMessage);
		this.chatMessages$.next([]);
		this.summary$.next("");
		this.feedbackAllowed = chatGuidObject.feedbackSystemAvailable && chatGuidObject.actions.CREATE_FEEDBACK;
	}

	onSubmit(value: string): void {
		if (!this.chatGuid) {
			return;
		}
		this.errorMessage = undefined;
		this.warningMessage = undefined;
		this.pushChatMessage(this.chatGuid, "REQUEST", this.form.get(this.selectionInput!.key)?.value[0], value, {});
		this.sessionStorageService.setValue(ChatbotComponent.CHAT_ID_PREF_KEY, {
			chatGuid: this.chatGuid,
			viewId: this.selectedViewId,
		});
		let response: string = "";
		let wordCount = 0;
		this.isLoading$.next(true);
		this.isStreaming$.next(true);
		this.streamingService
			.stream(
				this.currentWorkspaceId!,
				this.currentProcessVersion!,
				this.chatGuid,
				this.form.get(this.selectionInput!.key)?.value[0],
				value,
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (event) => {
					this.isLoading$.next(false);
					const isScrolledToBottom = this.isScrolledToBottom();
					switch (event.type) {
						case MessageType.CHUNK: {
							const doReformat = this.doReformat(response, event.chunk, wordCount++);
							response += event.chunk;
							if (doReformat) {
								wordCount = 0;
								this.reformatAndUpdate(response);
							}
							break;
						}
						case MessageType.EVIDENCES: {
							this.evidences = event.evidences ?? [];
							break;
						}
						case MessageType.DOWNLOADS: {
							this.downloads = event.downloads ?? [];
							break;
						}
						case MessageType.RESEND: {
							response = "";
							break;
						}
						case MessageType.COMPLETE: {
							if (event.summary) {
								this.summary$.next(event.summary);
							}
							break;
						}
					}
					if (isScrolledToBottom) {
						this.scrollToBottom();
					}
				},
				complete: () => {
					if (!this.chatGuid || !this.selectedViewId) {
						return;
					}
					this.pushChatMessage(
						this.chatGuid,
						"RESPONSE",
						this.selectedViewId,
						response,
						{ CREATE_FEEDBACK: this.feedbackAllowed },
						this.evidences,
						this.downloads,
					);
					this.isLoading$.next(false);
					this.isStreaming$.next(false);
					this.responseStream = Promise.resolve("");
				},
				error: (error: unknown) => this.onError(error as ErrorMessage),
			});
	}

	reformatAndUpdate(response: string): void {
		const reformattedResponse = marked(response);
		this.responseStream = Promise.resolve(reformattedResponse);
	}

	onError(errorMessage: ErrorMessage): void {
		void this.abort(false);
		console.error(
			`Error while streaming chat response -> Type: "${errorMessage.type}". Message: "${errorMessage.message}"`,
		);

		if (errorMessage.type === "SYSTEM") {
			this.showError();
		} else {
			this.showWarning(this.getWarningMessage(errorMessage.type));
		}
	}

	private getWarningMessage(type: string): string {
		switch (type) {
			case "ABORT": {
				return "chatbot.warning.abort";
			}
			case "SEARCH": {
				return "chatbot.warning.search";
			}
			case "BUDGET": {
				return "chatbot.warning.budget";
			}
			case "INDEX": {
				return "chatbot.warning.training";
			}
			case "FILTER": {
				return "chatbot.warning.filter";
			}
			case "TOKEN": {
				return "chatbot.warning.token";
			}
		}
		return "chatbot.warning.aisystem";
	}

	async abort(keepMessage: boolean): Promise<void> {
		this.isLoading$.next(false);
		this.isStreaming$.next(false);
		this.streamingService.abort();

		if (keepMessage) {
			const abortedChatResponse = await this.chatbotResource.abortStream(this.chatGuid!);
			if (abortedChatResponse === null) {
				const response = await this.responseStream;
				if (response !== undefined && response.length > 0 && this.chatGuid !== undefined) {
					this.pushChatMessage(
						this.chatGuid,
						"RESPONSE",
						this.selectedViewId!,
						response,
						{ CREATE_FEEDBACK: this.feedbackAllowed },
						this.evidences,
						this.downloads,
					);
				}
			} else {
				this.pushChatMessage(
					abortedChatResponse.chatId,
					"RESPONSE",
					abortedChatResponse.viewId,
					abortedChatResponse.messageText,
					{ CREATE_FEEDBACK: this.feedbackAllowed },
				);
			}
		}
		this.responseStream = Promise.resolve("");
		this.scrollToBottom();
	}

	private pushChatMessage(
		chatId: string,
		type: "REQUEST" | "RESPONSE",
		viewId: string,
		value: string,
		actions: { [key: string]: boolean },
		evidences?: Evidence[],
		downloads?: Download[],
	): void {
		const isScrolledToBottom = this.isScrolledToBottom();
		const chatMessage: FormattedChatMessage = this.formatChatMessage({
			chatId: chatId,
			type: type,
			messageText: value,
			viewId: viewId,
			evidences: evidences ?? [],
			downloads: downloads ?? [],
			actions: actions,
		});
		this.chatMessages$.pipe(take(1)).subscribe((currentMessages) => {
			this.chatMessages$.next([...currentMessages, chatMessage]);
			if (type === "REQUEST" || isScrolledToBottom) {
				this.scrollToBottom();
			}
		});
	}

	private isScrolledToBottom(): boolean {
		const obj = this.chatbotPanel().nativeElement;
		return obj.scrollTop > 0 && obj.scrollTop >= obj.scrollHeight - obj.offsetHeight - ChatbotComponent.SNAPIN_MARGIN;
	}

	private scrollToBottom(): void {
		setTimeout(() => {
			const obj = this.chatbotPanel().nativeElement;
			obj.scrollTop = obj.scrollHeight;
		}, 0);
	}

	protected async downloadFile(download: Download): Promise<void> {
		const openNewTab = download.fileAccessType === "LINK";
		const assureAuth = download.fileAccessType !== "LINK";
		await this.fileService.download(
			this.route,
			download.fileId,
			download.workspaceId,
			"_vv",
			null,
			openNewTab,
			assureAuth,
		);
	}

	private showWarning(warningMessage: string): void {
		if (this.errorMessage) {
			return;
		}
		this.warningMessage = warningMessage;
	}

	private showError(): void {
		if (this.warningMessage) {
			this.warningMessage = undefined;
		}
		this.errorMessage = ChatbotComponent.ERROR_MESSAGE_SYSTEM;
	}

	private toggleSelectionInput(hide: boolean): void {
		if (hide) {
			this.form.get(this.selectionInput!.key)?.disable({ emitEvent: false });
		} else {
			this.form.get(this.selectionInput!.key)?.enable({ emitEvent: false });
		}
	}

	protected reduceChatbotSize($event: MouseEvent, forceTrigger: boolean = false): void {
		if (
			forceTrigger ||
			($event.target as HTMLElement).className === "chatbot-link" ||
			($event.target as HTMLElement).className === "evidence-link" ||
			($event.target as HTMLElement).className === "evidence-name"
		) {
			this.layoutService.breakpoint.pipe(take(1)).subscribe((breakpoint) => {
				if (breakpoint.name === Size.XXSMALL) {
					// TODO: The 'emit' function requires a mandatory void argument
					this.closeButtonPressed.emit();
				}
			});
		}
	}

	getEvidenceLink(evidence: Evidence): unknown[] {
		return [
			"/",
			"workspace",
			evidence.workspaceId,
			evidence.processVersion,
			"process",
			evidence.elementTypeIdent,
			evidence.elementIdentity,
		];
	}

	private getSelectedView(viewId: string, views: ColoredOption[]): ColoredOption {
		return views.find((view) => view.id === viewId)!;
	}

	/**
	 * Decides to reformat current streaming output,
	 * if the sentence ends with a dot character, except when it is part of numeric enumerations,
	 * or if the sentence is divided by a comma,
	 * or if the reformat limit has been reached, except when the direct next chunk is a markdown control character.
	 */
	private doReformat(response: string, nextChunk: string | undefined, wordCount: number): boolean {
		return (
			nextChunk === undefined ||
			(nextChunk.endsWith(".") && Number.isNaN(Number(response.charAt(response.length - 1)))) ||
			nextChunk.endsWith(",") ||
			(wordCount > ChatbotComponent.REFORMAT_LIMIT && !nextChunk.search(/[#*>_—]/))
		);
	}

	formatChatMessage(chatMessage: ChatMessage): FormattedChatMessage {
		return {
			...chatMessage,
			formattedValue:
				chatMessage.type === "RESPONSE" ? marked(chatMessage.messageText, { async: false }) : chatMessage.messageText,
		};
	}
	openChildContainer(showContainer: ChatbotChildContainer, hideContainer: ChatbotChildContainer): void {
		if (showContainer === ChatbotChildContainer.CHATBOT) {
			const chatbotElement = document.getElementById(showContainer);
			const settingsElement = document.getElementById(hideContainer);
			if (chatbotElement && settingsElement) {
				chatbotElement.style.transform = "translateX(0%)";
				settingsElement.style.transform = "translateX(0%)";

				const onTransitionEnd = (): void => {
					this.settingsOpen = false;
					chatbotElement.removeEventListener("transitionend", onTransitionEnd);
				};
				chatbotElement.addEventListener("transitionend", onTransitionEnd);
			}
		} else {
			if (showContainer === ChatbotChildContainer.MENU) {
				this.settingsOpen = true;
			}

			setTimeout(() => {
				const chatbotElement = document.getElementById(hideContainer);
				const settingsElement = document.getElementById(showContainer);

				if (chatbotElement && settingsElement) {
					chatbotElement.style.transform = "translateX(-100%)";
					settingsElement.style.transform = "translateX(-100%)";
				}
			}, 10);
		}
	}

	setAiAcknowledgementsState(value: AiAcknowledgementsState): void {
		this._aiAcknowledgementsState = value;
		if (value === "DECLINED") {
			// TODO: The 'emit' function requires a mandatory void argument
			this.closeButtonPressed.emit();
		}
	}

	protected isChatbotFunctionalityAvailable(): boolean {
		return this.selectableViews.length > 0;
	}
}

function setupSelectionOptions(views: ColoredOption[]): SelectionOption[] {
	const selectionOptionsTemp: SelectionOption[] = [];
	for (const view of views) {
		if (view.id) {
			selectionOptionsTemp.push({
				value: view.id,
				untranslatableName: view.name,
			});
		}
	}
	return selectionOptionsTemp;
}
